import { FC, useEffect, useMemo, useState } from 'react';
import { DeviceToken, User } from '../../../types/User';
import { Button, ButtonStyles, Dropdown, Icons, SliderTab, SliderTabProps } from '@silverfort/sf-components';
import TokensTable, { TokensTableProps } from './TokensTable';
import TokensFooter, { TokensFooterProps } from './TokensFooter';
import { TokenState } from '../types';
import { getTokensPerState } from '../utils';
import { getObjectEntries } from '../../../../../utils';
import { useUsers } from '../../../context/UserManagementContext';
import { useTestTokensPopup } from '../hooks/useTestTokensPopup';
import { UserSession } from '../../../../../api';

type UserToTokens = {
    displayName: User['displayName'];
    tokens: User['tokens'];
    guid: User['guid'];
};

export interface TokensProps extends Pick<TokensTableProps, 'onTokenRemoved'> {
    tokenToFocus?: DeviceToken['id'];
    primaryUserToTokens: UserToTokens;
    secondaryUsersToToken: UserToTokens[];
    userSession: UserSession;
}

const getOptionForTokenToFocus = (
    tokenToFocus: DeviceToken['id'] | undefined,
    tokensPerState: Record<TokenState, DeviceToken[]>
) => {
    for (const [state, tokens] of getObjectEntries(tokensPerState)) {
        if (tokens.find((token) => token.id === tokenToFocus)) {
            return state;
        }
    }

    return TokenState.ACTIVE;
};

const getUserOptions = (users: UserToTokens[]) => {
    return users
        .map((user) => ({
            title: user.displayName,
            value: user.guid
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
};

const Tokens: FC<TokensProps> = ({
    primaryUserToTokens,
    secondaryUsersToToken,
    tokenToFocus,
    onTokenRemoved,
    userSession
}) => {
    const { systemData } = useUsers();
    const [expanded, setExpanded] = useState(true);

    const [selectedUserGuid, setSelectedUserGuid] = useState<string>(primaryUserToTokens.guid);

    const isTokenSharingEnabled = systemData?.Settings.IsTokenSharingEnabled ?? false;
    const isAccountMatchingEnabled = !!systemData?.Settings.AccountMatchingStrategy;

    const shouldDisplaySecondaryUsersDropdown =
        !isTokenSharingEnabled && isAccountMatchingEnabled && secondaryUsersToToken.length > 0;

    const combinedUserToTokens = useMemo(
        () => [primaryUserToTokens, ...secondaryUsersToToken],
        [primaryUserToTokens, secondaryUsersToToken]
    );

    const userSelectionOptions = useMemo(() => getUserOptions(combinedUserToTokens), [combinedUserToTokens]);
    const tokensPerUserGuid = useMemo(() => {
        const user = combinedUserToTokens.find((user) => user.guid === selectedUserGuid);
        return getTokensPerState(user?.tokens ?? []);
    }, [combinedUserToTokens, selectedUserGuid]);

    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState(
        getOptionForTokenToFocus(tokenToFocus, tokensPerUserGuid)
    );
    const tokensToDisplay = tokensPerUserGuid[selectedSwitcherOption];
    const tokens = useMemo(
        () => [...primaryUserToTokens.tokens, ...secondaryUsersToToken.flatMap(({ tokens }) => tokens)],
        [primaryUserToTokens.tokens, secondaryUsersToToken]
    );

    const { openTestTokensPopup } = useTestTokensPopup({ tokens });

    const handleToggleExpand = () => {
        setExpanded((prevOpen) => !prevOpen);
    };

    const handleSwitcherSelect = (option: TokenState) => {
        setSelectedSwitcherOption(option);
    };

    const handleTestAll = () => {
        openTestTokensPopup();
    };

    const handleDisableAll = () => {
        // todo: implement in the relevant US
    };

    const handleEnableAll = () => {
        // todo: implement in the relevant US
    };

    const handleUserSelectionChanged = (guid: string) => {
        setSelectedUserGuid(guid);
    };

    const switcherOptions: SliderTabProps<TokenState>['items'] = [
        {
            id: TokenState.ACTIVE,
            label: `Active (${tokensPerUserGuid[TokenState.ACTIVE].length})`
        },
        {
            id: TokenState.UNUSED,
            label: `Unused (${tokensPerUserGuid[TokenState.UNUSED].length})`,
            tooltipProps: {
                title: 'Unused tokens are tokens that were not used for authentication in the last 6 months.',
                placement: 'top-start'
            }
        }
        // Currently commented out as the feature is not implemented yet, will implement in the v6.1.1
        // {
        //     id: TokenState.DISABLED,
        //     label: `Disabled (${tokensPerUserGuid[TokenState.DISABLED].length})`
        // }
    ];

    const optionsToFooterActionProps: Record<
        TokenState,
        Pick<TokensFooterProps, 'actionTitle' | 'onActionClick' | 'requiredPermission' | 'isVisible'>
    > = {
        [TokenState.ACTIVE]: {
            actionTitle: 'Test all',
            onActionClick: handleTestAll,
            requiredPermission: 'testTokens',
            isVisible: true
        },
        [TokenState.UNUSED]: {
            actionTitle: 'Disable all',
            onActionClick: handleDisableAll,
            requiredPermission: 'disableToken',
            // Currently hidden because the "Disable Tokens" feature is not yet implemented.
            // TODO: Remove `isVisible` override once the feature is added in v6.1.1.
            isVisible: false
        },
        [TokenState.DISABLED]: {
            actionTitle: 'Enable all',
            onActionClick: handleEnableAll,
            requiredPermission: 'disableToken',
            isVisible: true
        }
    };

    const footerActionProps = optionsToFooterActionProps[selectedSwitcherOption];

    useEffect(() => {
        if (tokenToFocus) {
            setSelectedSwitcherOption(getOptionForTokenToFocus(tokenToFocus, tokensPerUserGuid));
        }
    }, [tokenToFocus, tokensPerUserGuid]);

    return (
        <div className="rounded-[8px] border border-sf-gray-100">
            <div
                className={`px-4 h-14 flex justify-between items-center ${
                    expanded ? 'border-b border-sf-gray-100' : ''
                }`}>
                <span className="font-sf font-medium text-13 leading-[15.87px]">Tokens</span>

                <div className="flex flex-row gap-2 items-center">
                    {shouldDisplaySecondaryUsersDropdown && (
                        <Dropdown
                            size="small"
                            variant="trigger"
                            className="[&]:h-6 w-[160px] rounded-md border bg-transparent [&]:border-sf-gray-400"
                            items={userSelectionOptions}
                            // @ts-ignore - todo: try to remove after - https://silverfort.atlassian.net/browse/AF-35919
                            onChange={handleUserSelectionChanged}
                            selectedItems={selectedUserGuid}
                        />
                    )}
                    <Button
                        iconRight={Icons.ChevronUp}
                        onClick={handleToggleExpand}
                        buttonStyle={ButtonStyles.TertiarySolid}
                    />
                </div>
            </div>
            <div
                className={`overflow-hidden transition-[max-height] ease-in-out duration-500 ${
                    expanded ? 'max-h-[1000px]' : 'max-h-0'
                }`}>
                <div className="flex flex-col gap-4 p-4">
                    <SliderTab
                        items={switcherOptions}
                        selectedId={selectedSwitcherOption}
                        onSelect={handleSwitcherSelect}
                    />
                    <TokensTable
                        tokens={tokensToDisplay}
                        tokenToFocus={tokenToFocus}
                        onTokenRemoved={onTokenRemoved}
                        userSession={userSession}
                    />
                    <TokensFooter minimumTokens={primaryUserToTokens.tokens.length} {...footerActionProps} />
                </div>
            </div>
        </div>
    );
};

export default Tokens;
